import { CommonModule, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ApiError, HttpService } from '../../../services/http.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface YearLevelData {
  id: string;
  yearLevel: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: YearLevelData;
}

@Component({
  selector: 'app-add-year-level-dialog',
  standalone: true,
  templateUrl: './add-year-level.component.html',
  styleUrls: ['./add-year-level.component.scss'],
  imports: [CommonModule, NgIf, ReactiveFormsModule, InputComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AddYearLevelDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  isEdit: boolean = false;
  viewMode: boolean = false;

  yearLevelData: YearLevelData | null = null;
  private destroy$ = new Subject<void>();
 submitInProgress: boolean = false;

  readonly customErrorMessages = {
    yearLevel: {
      required: 'Year Level is required.',
      minlength: 'Year level name must be at least 2 characters.',
      maxlength: 'Year level name cannot exceed 50 characters.',
    },
  };

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddYearLevelDialogComponent>,
    private dialog: MatDialog,
    private userProfileService: UserProfileService,
    @Inject(MAT_DIALOG_DATA) public data: YearLevelData | null,
  ) {
    this.initForm();

    if (data && data.id) {
      this.isEdit = true;
      this.yearLevelData = data;
    }
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    if (this.isEdit && this.yearLevelData) {
      this.patchFormWithData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      yearLevel: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
    });
  }

  private patchFormWithData(): void {
    if (this.yearLevelData) {
      this.form.patchValue({
        yearLevel: this.yearLevelData.yearLevel || '',
      });
      if (this.viewMode) {
        this.form.disable();
      }
      this.cdr.detectChanges();
    }
  }

  onCancel(): void {
    if (this.loading || this.submitInProgress) {
      return;
    }
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (
      this.loading ||
      this.submitInProgress ||
      this.viewMode ||
      this.form.invalid
    ) {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
      }
      return;
    }
    this.submitInProgress = true;
    this.loading = true;
    this.dialogRef.disableClose = true;
    this.form.disable();

    const payload = {
      year_level_name: this.form.get('yearLevel')?.value?.trim(),
    };

    const endpoint = this.isEdit
      ? `settings/yearlevel/${this.yearLevelData?.id}`
      : `settings/yearlevel`;

    const request = this.isEdit
      ? this.httpService.put<ApiDataResponse>(endpoint, payload)
      : this.httpService.post<ApiDataResponse>(endpoint, payload);

    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.submitInProgress = false;
          this.dialogRef.disableClose = false;

          if (this.dialogRef.getState() === 0) {
            this.form.enable();
          }
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => this.handleSubmitResponse(response),
        error: (err: ApiError) => this.handleError(err),
      });
  }

  private handleSubmitResponse(response: ApiDataResponse): void {
    if (response?.status) {
      const message = this.isEdit
        ? 'Year level updated successfully'
        : 'Year level added successfully';

      this.dialogRef.close({
        success: true,
        data: response.data,
      });
      setTimeout(() => {
        this.showSuccessToast(response.message || message);
      }, 100);
    } else {
      this.showErrorToast(
        response?.message || 'Something went wrong. Please try again later.',
      );
    }
  }

  private handleError(err: ApiError): void {
    if (this.dialogRef.getState() === 0) {
      this.form.enable();
    }

    if (err?.errors && typeof err.errors === 'object') {
      Object.entries(err.errors).forEach(([field, fieldErrors]) => {
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach((message) => {
            this.showErrorToast(message);
          });
        } else if (typeof fieldErrors === 'string') {
          this.showErrorToast(fieldErrors);
        }
      });
      return;
    }

    const errorMessage = this.getErrorMessage(err);
    this.showErrorToast(errorMessage);
  }

  private getErrorMessage(err: any): string {
    if (err?.message) return err.message;
    if (err?.status === 409) return 'Year level already exists.';
    if (err?.status === 400)
      return 'Invalid data provided. Please check your input.';
    if (err?.status === 500) return 'Server error. Please try again later.';
    return 'Something went wrong. Please try again later.';
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;

    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'error' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-toast'],
    });
  }
}
